import { useMemo, useState } from 'react';
import { Table, Button, Input, Badge } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useTenantContext } from '../../auth/useTenantContext';
import { useRole } from '../../auth/useRole';
import { useTenantAdminWarehouses, type Warehouse } from '../../api/adminWarehouses';
import { AdminWarehouseDrawer } from '../../components/admin/AdminWarehouseDrawer';
import { PageHeading } from '../../layouts/PageHeading';
import { useFilterSidebar } from '../../layouts/FilterSidebarContext';
import { PageFilters, FilterSection } from '../../components/ui/PageFilters';

// SP-5m: master-detail edit-accordion. Either the "create" panel is open, or
// one row is expanded for editing (single-open).
type Open = null | 'create' | number;

export default function WarehousesPage() {
  const { t } = useTranslation('warehouse');
  const { token, tenantCode } = useTenantContext();
  // Read-only when caller has no write-capable role. A user who is BOTH
  // tenant-admin and voice-supervisor keeps full write controls.
  const { isAnyAdmin } = useRole();
  const readOnly = !isAnyAdmin;
  const { data, isLoading } = useTenantAdminWarehouses(token, tenantCode);
  const [open, setOpen] = useState<Open>(null);
  const [search, setSearch] = useState('');

  const filtered = (data?.data ?? []).filter((w) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return w.code.toLowerCase().includes(s) || w.name.toLowerCase().includes(s);
  });

  // SP-8a — sidebar filtri.
  const sidebar = useMemo(() => (
    <PageFilters footer={`${filtered.length} di ${data?.data.length ?? 0} magazzini`}>
      <FilterSection title="Cerca">
        <Input
          type="search"
          placeholder="Code o nome"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </FilterSection>
    </PageFilters>
  ), [search, filtered.length, data?.data.length]);
  useFilterSidebar(sidebar);

  const COLS = 4;

  return (
    <div>
      <PageHeading
        scope="Magazzini"
        readOnly={readOnly}
        actions={!readOnly ? (
          <Button color="primary" onClick={() => setOpen((o) => (o === 'create' ? null : 'create'))}>
            <i className="bi bi-plus-lg me-1" />+ Nuovo magazzino
          </Button>
        ) : undefined}
      />

      {!readOnly && open === 'create' && (
        <AdminWarehouseDrawer mode="create" isOpen onClose={() => setOpen(null)} />
      )}

      {isLoading ? (
        <p className="text-muted">Caricamento…</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted">{readOnly ? 'Nessun magazzino nel tenant.' : 'Nessun magazzino. Crea il primo con "+ Nuovo magazzino".'}</p>
      ) : (
        <div className="table-responsive">
        <Table className="table-hover align-middle table-striped mb-0">
          <thead>
            <tr><th>Code</th><th>Nome</th><th>Timezone</th><th>Indirizzo</th></tr>
          </thead>
          <tbody>
            {filtered.map((w: Warehouse) => {
              const isRowOpen = open === w.id;
              return (
                <FragmentRow key={w.id}>
                  <tr
                    className={isRowOpen ? 'table-active' : undefined}
                    style={readOnly ? undefined : { cursor: 'pointer' }}
                    onClick={readOnly ? undefined : () => setOpen((o) => (o === w.id ? null : w.id))}
                    aria-expanded={!readOnly ? isRowOpen : undefined}
                  >
                    <td className="font-monospace vp-code">
                      {w.code}
                      {/* SP-A T7b — un magazzino sospeso non accetta piu' liste
                          ne' operatori: deve vedersi dall'elenco, non solo
                          entrando nel dettaglio. */}
                      {w.status === 'suspended' && (
                        <Badge color="warning" pill className="ms-2 align-middle">
                          {t('status.suspended')}
                        </Badge>
                      )}
                    </td>
                    <td>{w.name}</td>
                    <td>{w.timezone}</td>
                    <td>{w.address ?? '—'}</td>
                  </tr>
                  {!readOnly && isRowOpen && (
                    <tr className="wh-edit-row">
                      <td colSpan={COLS} className="p-0 border-0">
                        <AdminWarehouseDrawer
                          mode="edit" isOpen
                          initial={{ ...w, address: w.address ?? null, status: w.status ?? 'active', sftp_inbound_folder: w.sftp_inbound_folder ?? null }}
                          onClose={() => setOpen(null)}
                        />
                      </td>
                    </tr>
                  )}
                </FragmentRow>
              );
            })}
          </tbody>
        </Table>
        </div>
      )}
    </div>
  );
}

function FragmentRow({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
